import api from './api';

// API endpoints for producer management
const API_ENDPOINTS = {
  PRODUCER: '/admin/producer',
};

// Define the Producer interface
export interface Producer {
  _id: string;
  title?: string;
  name: string;
  fullName: string;
  shortName?: string;
  country: string;
  region?: string;
  needsReview?: boolean;
  createdAt: string;
  updatedAt: string;
}

// Define pagination metadata interface
export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

// Define paginated response interface
export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

// Define country with count interface
export interface ProducerCountry {
  country: string;
  count: number;
}

const producerService = {
  /**
   * Get all producers
   * @param page Page number (1-based)
   * @param pageSize Number of items per page
   * @param sort Field to sort by
   * @param order Sort order ('asc' or 'desc')
   * @param searchQuery Optional search query for Atlas Search
   * @param countries Optional array of country codes to filter by
   * @returns Paginated response with all producers
   */
  getAllProducers: async (
    page = 1,
    pageSize = 20,
    sort = 'name',
    order = 'asc',
    searchQuery?: string,
    countries?: string[],
    needsReview?: boolean
  ): Promise<PaginatedResponse<Producer>> => {
    const params: any = { page, pageSize, sort, order };
    if (searchQuery?.trim()) {
      params.q = searchQuery.trim();
    }
    if (countries && countries.length > 0) {
      params.countries = countries.join(',');
    }
    if (needsReview !== undefined) {
      params.needsReview = needsReview;
    }

    const response = await api.get(API_ENDPOINTS.PRODUCER, { params });
    return response.data;
  },

  /**
   * Search producers using Atlas Search
   * @param query Search query
   * @param page Page number (1-based)
   * @param pageSize Number of items per page
   * @returns Paginated search results
   */
  searchProducers: async (query: string, page = 1, pageSize = 20): Promise<PaginatedResponse<Producer>> => {
    const response = await api.get(API_ENDPOINTS.PRODUCER, {
      params: { q: query.trim(), page, pageSize }
    });
    return response.data;
  },

  /**
   * Get a specific producer by ID
   */
  getProducer: async (id: string): Promise<Producer> => {
    const response = await api.get(`${API_ENDPOINTS.PRODUCER}/${id}`);
    return response.data;
  },

  /**
   * Create a new producer
   */
  createProducer: async (producer: Omit<Producer, '_id' | 'createdAt' | 'updatedAt'>): Promise<Producer> => {
    const response = await api.post(API_ENDPOINTS.PRODUCER, producer);
    return response.data;
  },

  /**
   * Update an existing producer
   */
  updateProducer: async (id: string, producer: Partial<Omit<Producer, '_id' | 'createdAt' | 'updatedAt'>>): Promise<Producer> => {
    const response = await api.put(`${API_ENDPOINTS.PRODUCER}/${id}`, producer);
    return response.data;
  },

  /**
   * Delete a producer
   */
  deleteProducer: async (id: string): Promise<void> => {
    await api.delete(`${API_ENDPOINTS.PRODUCER}/${id}`);
  },

  /**
   * Get producers by country
   */
  getProducersByCountry: async (country: string, page = 1, pageSize = 20, sort = 'name', order = 'asc'): Promise<PaginatedResponse<Producer>> => {
    const response = await api.get(`${API_ENDPOINTS.PRODUCER}/country/${country}`, {
      params: { page, pageSize, sort, order }
    });
    return response.data;
  },

  /**
   * Get producers by region
   */
  getProducersByRegion: async (region: string, page = 1, pageSize = 20, sort = 'name', order = 'asc'): Promise<PaginatedResponse<Producer>> => {
    const response = await api.get(`${API_ENDPOINTS.PRODUCER}/region/${region}`, {
      params: { page, pageSize, sort, order }
    });
    return response.data;
  },

  /**
   * Get available countries from producers
   * @returns List of countries with producer counts
   */
  getAvailableCountries: async (): Promise<ProducerCountry[]> => {
    const response = await api.get(`${API_ENDPOINTS.PRODUCER}/countries`);
    return response.data.data;
  }
};

export default producerService;